import React, { useEffect, useState } from "react";
import { api, type Prompt } from "../lib/api.ts";

export default function Settings() {
  const [settings, setSettings] = useState<Record<string, string>>({});
  const [prompts, setPrompts]   = useState<Prompt[]>([]);
  const [loading, setLoading]   = useState(true);
  const [saving, setSaving]     = useState(false);
  const [editPrompt, setEditPrompt] = useState<Prompt | null>(null);
  const [editBody, setEditBody] = useState("");

  useEffect(() => {
    Promise.all([api.settings.get(), api.prompts.list()])
      .then(([s, p]) => { setSettings(s); setPrompts(p); })
      .catch(console.error)
      .finally(() => setLoading(false));
  }, []);

  async function handleSaveSettings(e: React.FormEvent) {
    e.preventDefault();
    setSaving(true);
    try {
      await api.settings.update(settings);
    } catch (err) {
      alert(String(err));
    } finally { setSaving(false); }
  }

  async function handleSavePrompt() {
    if (!editPrompt) return;
    setSaving(true);
    try {
      const updated = await api.prompts.upsert(editPrompt.name, editBody);
      setPrompts(prev => prev.map(p => p.name === editPrompt.name ? updated : p));
      setEditPrompt(null);
    } catch (err) {
      alert(String(err));
    } finally { setSaving(false); }
  }

  const field = (key: string, label: string, type = "text") => (
    <div key={key}>
      <label className="label">{label}</label>
      <input
        className="input text-sm"
        type={type}
        value={settings[key] ?? ""}
        onChange={e => setSettings(s => ({ ...s, [key]: e.target.value }))}
      />
    </div>
  );

  if (loading) return <div className="p-6 text-gray-400 text-sm">Loading…</div>;

  return (
    <div className="p-6 max-w-2xl">
      <h1 className="text-2xl font-semibold text-gray-900 mb-5">Settings</h1>

      <form onSubmit={handleSaveSettings} className="card p-5 space-y-4 mb-6">
        <h2 className="font-medium text-gray-900">AI Provider</h2>
        <div>
          <label className="label">Default Rewrite Provider</label>
          <select className="input text-sm" value={settings["ai_default_rewrite_provider"] ?? "huggingface"}
            onChange={e => setSettings(s => ({ ...s, ai_default_rewrite_provider: e.target.value }))}>
            <option value="huggingface">HuggingFace Inference (free tier)</option>
            <option value="openai">OpenAI (paid)</option>
            <option value="anthropic">Anthropic (paid)</option>
          </select>
        </div>
        <div>
          <label className="label">Default Rewrite Model</label>
          <div className="flex gap-2">
            <select className="input text-sm flex-1"
              value={settings["ai_default_rewrite_model"] ?? "Qwen/Qwen2.5-7B-Instruct"}
              onChange={e => setSettings(s => ({ ...s, ai_default_rewrite_model: e.target.value }))}>
              <option value="Qwen/Qwen2.5-7B-Instruct">Qwen 2.5 7B (cheap — ~$0.0004/req)</option>
              <option value="meta-llama/Llama-3.1-8B-Instruct">Llama 3.1 8B (mid — ~$0.0004/req)</option>
              <option value="moonshotai/Kimi-K2.6">Kimi K2.6 (premium — ~$0.009/req)</option>
            </select>
          </div>
        </div>
        <div>
          <label className="label">Provider Selection Strategy</label>
          <select className="input text-sm"
            value={settings["ai_provider_strategy"] ?? "cheapest"}
            onChange={e => setSettings(s => ({ ...s, ai_provider_strategy: e.target.value }))}>
            <option value="cheapest">Cheapest provider (default — lower cost)</option>
            <option value="fastest">Fastest provider (higher cost, better speed)</option>
          </select>
        </div>
        {field("polling_interval_minutes", "RSS Poll Interval (minutes)", "number")}
        <div>
          <label className="label">Citation Output Format</label>
          <select className="input text-sm" value={settings["citation_output_format"] ?? "plain_list"}
            onChange={e => setSettings(s => ({ ...s, citation_output_format: e.target.value }))}>
            <option value="plain_list">Plain list</option>
            <option value="numbered">Numbered footnotes</option>
            <option value="styled_block">Styled block</option>
          </select>
        </div>
        {field("d1_retention_days", "D1 Retention (days)", "number")}
        <button type="submit" disabled={saving} className="btn-primary btn">
          {saving ? "Saving…" : "Save Settings"}
        </button>
      </form>

      {/* Prompts */}
      <div className="card p-5">
        <h2 className="font-medium text-gray-900 mb-4">System Prompts</h2>
        <div className="space-y-3">
          {prompts.map(prompt => (
            <div key={prompt.name} className="border border-gray-200 rounded-lg p-3">
              <div className="flex items-center justify-between mb-1">
                <span className="font-mono text-sm text-gray-700">{prompt.name}</span>
                <button
                  onClick={() => { setEditPrompt(prompt); setEditBody(prompt.body); }}
                  className="btn-ghost btn btn-sm"
                >
                  Edit
                </button>
              </div>
              {editPrompt?.name === prompt.name ? (
                <div className="mt-2 space-y-2">
                  <textarea
                    value={editBody}
                    onChange={e => setEditBody(e.target.value)}
                    rows={8}
                    className="input text-sm font-mono resize-y"
                  />
                  <div className="flex gap-2">
                    <button onClick={handleSavePrompt} disabled={saving} className="btn-primary btn btn-sm">
                      {saving ? "Saving…" : "Save Prompt"}
                    </button>
                    <button onClick={() => setEditPrompt(null)} className="btn-ghost btn btn-sm">
                      Cancel
                    </button>
                  </div>
                </div>
              ) : (
                <p className="text-xs text-gray-400 truncate">{prompt.body.substring(0, 120)}…</p>
              )}
            </div>
          ))}
        </div>
      </div>

      {/* Triggers */}
      <div className="card p-5 mt-6">
        <h2 className="font-medium text-gray-900 mb-4">Admin Triggers</h2>
        <div className="space-y-3">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-700">Purge Error Articles</p>
              <p className="text-xs text-gray-400">Delete all articles stuck in error status</p>
            </div>
            <button
              onClick={async () => {
                if (!confirm("Delete all error-status articles? This cannot be undone.")) return;
                try {
                  const result = await api.triggers.purgeByStatus("error");
                  alert(result.message);
                } catch (err) {
                  alert(String(err));
                }
              }}
              className="btn-danger btn btn-sm"
            >
              Purge
            </button>
          </div>
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-700">Purge Content-Incomplete Articles</p>
              <p className="text-xs text-gray-400">Delete articles where content fetch failed</p>
            </div>
            <button
              onClick={async () => {
                if (!confirm("Delete all content_incomplete articles? This cannot be undone.")) return;
                try {
                  const result = await api.triggers.purgeByStatus("content_incomplete");
                  alert(result.message);
                } catch (err) {
                  alert(String(err));
                }
              }}
              className="btn-danger btn btn-sm"
            >
              Purge
            </button>
          </div>
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-700">Purge Pending-Rewrite Articles</p>
              <p className="text-xs text-gray-400">Delete articles waiting for AI rewrite</p>
            </div>
            <button
              onClick={async () => {
                if (!confirm("Delete all pending_rewrite articles? This cannot be undone.")) return;
                try {
                  const result = await api.triggers.purgeByStatus("pending_rewrite");
                  alert(result.message);
                } catch (err) {
                  alert(String(err));
                }
              }}
              className="btn-danger btn btn-sm"
            >
              Purge
            </button>
          </div>
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-700">Purge Content-Missing Articles</p>
              <p className="text-xs text-gray-400">Delete articles where original body content is empty</p>
            </div>
            <button
              onClick={async () => {
                if (!confirm("Delete all articles with missing content? This cannot be undone.")) return;
                try {
                  const result = await api.triggers.purgeContentMissing();
                  alert(result.message);
                } catch (err) {
                  alert(String(err));
                }
              }}
              className="btn-danger btn btn-sm"
            >
              Purge
            </button>
          </div>
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-700">Poll All RSS Feeds</p>
              <p className="text-xs text-gray-400">Fetch new articles from all active RSS feeds now</p>
            </div>
            <button
              onClick={async () => {
                try {
                  const result = await api.triggers.poll();
                  alert(result.message);
                } catch (err) {
                  alert(String(err));
                }
              }}
              className="btn-primary btn btn-sm"
            >
              Poll
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
